/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Textarea } from "@/components/ui/textarea"
import apiService from "@/services/apiService"

interface PoliceStation {
  id: string
  name: string
  nameEn?: string
  code: string
  district: string
  circle?: string
  isActive: boolean
}

interface User {
  id: string
  name: string
  email: string
  phone?: string
  badgeNumber: string
  role: string
  department?: string
  status: "ACTIVE" | "INACTIVE" | "SUSPENDED"
  bio?: string
  profilePicture?: string
  joinDate: string
  // Updated police station structure
  policeStationDetails?: PoliceStation[]
  policeStationIds?: string[]
  policeStationNames?: string[]
  password: string
  lastLogin?: string
  createdById?: string
}

interface UserFormPopupProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: User | null
  onSave: (userData: Omit<User, "id" | "lastLogin" | "createdById"> & { policeStationIds: string[] }) => void
}

const roles = [
  "ADMIN",
  "SHO",
  "CO",
  "SDM",
  "DM"
]

const departments = [
  "All",
  "Administration",
  "General Duty",
  "Investigation",
  "Forensic",
  "Crime Branch",
  "Traffic",
  "Patrol",
  "Investigation",
  "Security",
  "Cyber Crime",
  "Anti-Terrorism",
  "Narcotics",
  "Economic Offences",
]

export function UserFormPopup({ open, onOpenChange, user, onSave }: UserFormPopupProps) {
  const [policeStations, setPoliceStations] = useState<PoliceStation[]>([])
  const [loadingStations, setLoadingStations] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    badgeNumber: "",
    role: "",
    department: "",
    status: "ACTIVE" as "ACTIVE" | "INACTIVE" | "SUSPENDED",
    bio: "",
    profilePicture: "",
    joinDate: "",
    policeStationIds: [] as string[],
    password: ""
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Fetch police stations from API
  useEffect(() => {
    const fetchPoliceStations = async () => {
      try {
        setLoadingStations(true)
        const response:any = await apiService.get('police-stations')
        
        // Handle different API response structures
        let stations = response?.data || response || []
        
        // Ensure stations is an array
        if (!Array.isArray(stations)) {
          stations = []
        }
        
        setPoliceStations(stations)
      } catch (error) {
        console.error('Error fetching police stations:', error)
        setPoliceStations([])
      } finally {
        setLoadingStations(false)
      }
    }

    if (open) {
      fetchPoliceStations()
    }
  }, [open])

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        phone: user.phone || "",
        badgeNumber: user.badgeNumber,
        role: user.role,
        department: user.department || "",
        status: user.status,
        bio: user.bio || "",
        profilePicture: user.profilePicture || "",
        joinDate: user.joinDate ? user.joinDate.slice(0, 10) : "",
        policeStationIds: user.policeStationIds || [],
        password: user.password
      })
    } else {
      setFormData({
        name: "",
        email: "",
        phone: "",
        badgeNumber: "",
        role: "",
        department: "",
        status: "ACTIVE",
        bio: "",
        profilePicture: "",
        joinDate: new Date().toISOString().split("T")[0],
        policeStationIds: [],
        password: ""
      })
    }
    setErrors({})
    setSearchTerm("")
  }, [user, open])

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }
  }

  const handlePoliceStationToggle = (stationId: string) => {
    setFormData((prev) => ({
      ...prev,
      policeStationIds: prev.policeStationIds.includes(stationId)
        ? prev.policeStationIds.filter((id) => id !== stationId)
        : [...prev.policeStationIds, stationId]
    }))
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) newErrors.name = "Name is required"
    if (!formData.email.trim()) newErrors.email = "Email is required"
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email is invalid"
    if (!formData.role) newErrors.role = "Role is required"
    if (!formData.badgeNumber.trim()) newErrors.badgeNumber = "Badge number is required"
    if (formData.phone && !/^\+?[\d\s-()]+$/.test(formData.phone)) newErrors.phone = "Phone number is invalid"
    if (!formData.joinDate) newErrors.joinDate = "Join date is required"
    if (!user && !formData.password.trim()) newErrors.password = "Password is required for new users"
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (validateForm()) {
      const submitData = {
        ...formData,
        phone: formData.phone || undefined,
        department: formData.department || undefined,
        bio: formData.bio || undefined,
        profilePicture: formData.profilePicture || undefined,
        password: formData.password ?? "", // Always a string
      }
      onSave(submitData)
    }
  }

  // Filter stations based on search term
  const filteredStations = useMemo(() => {
    if (!policeStations || policeStations.length === 0) return []
    
    if (!searchTerm || !searchTerm.trim()) return policeStations

    const search = searchTerm.toLowerCase()

    return policeStations.filter(station =>
      (station.name && station.name.toLowerCase().includes(search)) ||
      (station.nameEn && station.nameEn.toLowerCase().includes(search)) ||
      (station.code && station.code.toLowerCase().includes(search)) ||
      (station.district && station.district.toLowerCase().includes(search))
    )
  }, [policeStations, searchTerm])

  // Group stations by circle for better organization
  const groupedStations = useMemo(() => {
    if (!filteredStations || filteredStations.length === 0) return {}

    return filteredStations.reduce((groups, station) => {
      const circle = (station.circle && station.circle.trim()) || "Other"
      if (!groups[circle]) {
        groups[circle] = []
      }
      groups[circle].push(station)
      return groups
    }, {} as Record<string, PoliceStation[]>)
  }, [filteredStations])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[70vw] max-w-none h-[80vh] bg-white flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold text-black">{user ? "Edit User" : "Create New User"}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 min-h-0">
          <div className="space-y-4 pb-4">
            {/* Personal Information */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-black">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      className={`border-gray-300 ${errors.name ? "border-red-500" : ""}`}
                      placeholder="Enter full name"
                    />
                    {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className={`border-gray-300 ${errors.email ? "border-red-500" : ""}`}
                      placeholder="Enter email address"
                    />
                    {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">Phone Number</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      className={`border-gray-300 ${errors.phone ? "border-red-500" : ""}`}
                      placeholder="+91 9876543210 (Optional)"
                    />
                    {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">
                      Join Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="date"
                      value={formData.joinDate}
                      onChange={(e) => handleInputChange("joinDate", e.target.value)}
                      className={`border-gray-300 ${errors.joinDate ? "border-red-500" : ""}`}
                    />
                    {errors.joinDate && <p className="text-red-500 text-xs">{errors.joinDate}</p>}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-black">Profile Picture URL</Label>
                  <Input
                    value={formData.profilePicture}
                    onChange={(e) => handleInputChange("profilePicture", e.target.value)}
                    className="border-gray-300"
                    placeholder="https://example.com/profile.jpg (Optional)"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-black">Bio</Label>
                  <Textarea
                    value={formData.bio}
                    onChange={(e) => handleInputChange("bio", e.target.value)}
                    className="border-gray-300 min-h-[80px]"
                    placeholder="Brief description about the officer (Optional)"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Professional Information */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-black">Professional Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">
                      Badge Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.badgeNumber}
                      onChange={(e) => handleInputChange("badgeNumber", e.target.value)}
                      className={`border-gray-300 ${errors.badgeNumber ? "border-red-500" : ""}`}
                      placeholder="Enter badge number"
                    />
                    {errors.badgeNumber && <p className="text-red-500 text-xs">{errors.badgeNumber}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">
                      Role <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.role} onValueChange={(value) => handleInputChange("role", value)}>
                      <SelectTrigger className={`border-gray-300 w-full ${errors.role ? "border-red-500" : ""}`}>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent className="bg-white w-full border border-gray-200">
                        {roles.map((role) => (
                          <SelectItem key={role} value={role} className="hover:bg-gray-50">
                            {role.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.role && <p className="text-red-500 text-xs">{errors.role}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">Department</Label>
                    <Select
                      value={formData.department}
                      onValueChange={(value) => handleInputChange("department", value)}
                    >
                      <SelectTrigger className={`border-gray-300 w-full ${errors.department ? "border-red-500" : ""}`}>
                        <SelectValue placeholder="Select department (Optional)" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border w-full border-gray-200">
                        {departments.map((dept, index: number) => (
                          <SelectItem key={dept + index} value={dept} className="hover:bg-gray-50">
                            {dept}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.department && <p className="text-red-500 text-xs">{errors.department}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value: "ACTIVE" | "INACTIVE" | "SUSPENDED") => handleInputChange("status", value)}
                    >
                      <SelectTrigger className="border-gray-300 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 w-full">
                        <SelectItem value="ACTIVE" className="hover:bg-gray-50">
                          Active
                        </SelectItem>
                        <SelectItem value="INACTIVE" className="hover:bg-gray-50">
                          Inactive
                        </SelectItem>
                        <SelectItem value="SUSPENDED" className="hover:bg-gray-50">
                          Suspended
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Police Station Assignment */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-black">Police Station Assignment</Label>
                  
                  {/* Search Box */}
                  <Input
                    placeholder="Search police stations by name, code, or district..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border-gray-300"
                  />
                  
                  {/* Station Selection */}
                  <div className="p-3 border border-gray-200 rounded-md bg-gray-50 max-h-64 overflow-y-auto">
                    {loadingStations ? (
                      <div className="text-center py-4 text-gray-500">Loading police stations...</div>
                    ) : filteredStations.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        {searchTerm ? `No stations found matching "${searchTerm}"` : "No police stations available"}
                      </div>
                    ) : Object.keys(groupedStations).length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        Unable to group stations
                      </div>
                    ) : (
                      Object.entries(groupedStations).map(([circle, stations]) => (
                        <div key={circle} className="mb-4 last:mb-0">
                          <h4 className="text-sm font-medium text-gray-700 mb-2 border-b pb-1">
                            {circle} ({stations.length})
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {stations.map((station) => {
                              const isChecked = formData.policeStationIds.includes(station.id)
                              return (
                                <label key={station.id} className="flex items-start space-x-2 cursor-pointer p-2 hover:bg-white rounded">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handlePoliceStationToggle(station.id)}
                                    className="rounded border-gray-300 mt-0.5"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-black truncate">
                                      {station.name}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {station.nameEn && `${station.nameEn} • `}
                                      {station.code} • {station.district}
                                    </div>
                                  </div>
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>Select police stations where this officer is assigned</span>
                    <span>{formData.policeStationIds.length} selected</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Security Information */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-black">Security Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-black">
                      Password {!user && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      className={`border-gray-300 ${errors.password ? "border-red-500" : ""}`}
                      placeholder={user ? "Leave blank to keep current password" : "Enter password"}
                    />
                    {errors.password && <p className="text-red-500 text-xs">{errors.password}</p>}
                    <p className="text-xs text-gray-600">
                      {user ? "Leave blank to keep current password or enter new password" : "Enter a secure password for the user"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        <div className="flex-shrink-0 flex justify-end space-x-2 pt-4 border-t border-gray-200 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-300">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            {user ? "Update User" : "Create User"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}